if (!customElements.get('byo-box')) {
  customElements.define(
    'byo-box',
    class BYOBuilder extends HTMLElement {
      constructor() {
        super();
        this.state = {
          packId: null,
          packSize: 0,
          selectedVariantId: null,
          selectedPrice: '',
          selectedCount: 0,
          products: {},
        };
        this.data = { packs: [], products: [] };
        this.isAdding = false;
      }

      connectedCallback() {
        this.readData();
        this.cacheDom();
        this._onClick = this.onClick.bind(this);
        this.addEventListener('click', this._onClick);
        this.selectPack(this.data.packs[0] && this.data.packs[0].id);
      }

      disconnectedCallback() {
        if (this._onClick) this.removeEventListener('click', this._onClick);
      }

      readData() {
        const script = this.querySelector('[data-byo-box-data]');
        if (!script) return;
        try {
          const parsed = JSON.parse(script.textContent);
          this.data = {
            packs: Array.isArray(parsed.packs) ? parsed.packs : [],
            products: Array.isArray(parsed.products) ? parsed.products : [],
          };
        } catch (error) {
          console.error('byo-box: invalid seed data', error);
        }
      }

      cacheDom() {
        this.packButtons = Array.from(this.querySelectorAll('[data-byo-pack]'));
        this.productCards = Array.from(this.querySelectorAll('[data-byo-product]'));
        this.selectedCountEls = Array.from(
          this.querySelectorAll('[data-byo-selected-count], [data-byo-summary-count]')
        );
        this.packSizeEls = Array.from(
          this.querySelectorAll('[data-byo-pack-size], [data-byo-summary-size]')
        );
        this.progressBar = this.querySelector('[data-byo-progress-bar]');
        this.progressFill = this.querySelector('[data-byo-progress-fill]');
        this.progressComplete = this.querySelector('[data-byo-progress-complete]');
        this.summaryPreview = this.querySelector('[data-byo-summary-preview]');
        this.summaryList = this.querySelector('[data-byo-summary-list]');
        this.summaryPanel = this.querySelector('[data-byo-summary-panel]');
        this.summaryToggle = this.querySelector('[data-byo-summary-toggle]');
        this.ctaButton = this.querySelector('[data-byo-add-to-cart]');
        this.ctaLabel = this.querySelector('[data-byo-cta-label]');
        this.ctaPrice = this.querySelector('[data-byo-cta-price]');
        this.errorEl = this.querySelector('[data-byo-error]');
      }

      onClick(event) {
        const packButton = event.target.closest('[data-byo-pack]');
        if (packButton) {
          this.selectPack(packButton.dataset.packId);
          return;
        }

        const increaseButton = event.target.closest('[data-byo-increase]');
        if (increaseButton) {
          const card = increaseButton.closest('[data-byo-product]');
          if (card) this.increase(card.dataset.productId);
          return;
        }

        const decreaseButton = event.target.closest('[data-byo-decrease]');
        if (decreaseButton) {
          const card = decreaseButton.closest('[data-byo-product]');
          if (card) this.decrease(card.dataset.productId);
          return;
        }

        const removeButton = event.target.closest('[data-byo-summary-remove]');
        if (removeButton) {
          this.removeProduct(removeButton.dataset.productId);
          return;
        }

        const summaryToggle = event.target.closest('[data-byo-summary-toggle]');
        if (summaryToggle) {
          this.toggleSummary();
          return;
        }

        const addToCartButton = event.target.closest('[data-byo-add-to-cart]');
        if (addToCartButton) {
          this.addToCart();
        }
      }

      selectPack(id) {
        const pack = this.data.packs.find((candidate) => candidate.id === id) || this.data.packs[0];
        if (!pack) return;

        this.state.packId = pack.id;
        this.state.packSize = pack.size;
        this.state.selectedVariantId = pack.variantId;
        this.state.selectedPrice = pack.price;
        this.state.products = {};
        this.state.selectedCount = 0;

        this.packButtons.forEach((button) => {
          const isActive = button.dataset.packId === pack.id;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-checked', String(isActive));
        });

        this.setSummaryExpanded(false);
        this.hideError();
        this.render();
      }

      increase(productId) {
        if (!productId || this.state.selectedCount >= this.state.packSize) return;
        this.state.products[productId] = (this.state.products[productId] || 0) + 1;
        this.state.selectedCount += 1;
        this.render();
      }

      decrease(productId) {
        if (!productId) return;
        const qty = this.state.products[productId] || 0;
        if (qty <= 0) return;
        if (qty === 1) {
          delete this.state.products[productId];
        } else {
          this.state.products[productId] = qty - 1;
        }
        this.state.selectedCount -= 1;
        this.render();
      }

      removeProduct(productId) {
        if (!productId || !this.state.products[productId]) return;
        this.state.selectedCount -= this.state.products[productId];
        delete this.state.products[productId];
        this.render();
      }

      validate() {
        return this.state.packSize > 0 && this.state.selectedCount === this.state.packSize;
      }

      render() {
        this.updateProgress();
        this.updateCards();
        this.updateSummary();
      }

      updateProgress() {
        this.selectedCountEls.forEach((el) => {
          el.textContent = this.state.selectedCount;
        });
        this.packSizeEls.forEach((el) => {
          el.textContent = this.state.packSize;
        });

        const percent = this.state.packSize > 0
          ? Math.min(100, Math.round((this.state.selectedCount / this.state.packSize) * 100))
          : 0;

        if (this.progressFill) this.progressFill.style.width = `${percent}%`;
        if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(percent));
        if (this.progressComplete) this.progressComplete.hidden = !this.validate();
      }

      updateCards() {
        const atMax = this.state.selectedCount >= this.state.packSize;
        this.productCards.forEach((card) => {
          const qty = this.state.products[card.dataset.productId] || 0;
          const qtyEl = card.querySelector('[data-byo-qty]');
          const decreaseButton = card.querySelector('[data-byo-decrease]');
          const increaseButton = card.querySelector('[data-byo-increase]');

          if (qtyEl) qtyEl.textContent = qty;
          card.classList.toggle('has-qty', qty > 0);
          if (decreaseButton) decreaseButton.disabled = qty <= 0;
          if (increaseButton) increaseButton.disabled = atMax;
        });
      }

      updateSummary() {
        const productMap = new Map(this.data.products.map((product) => [String(product.id), product]));
        const entries = Object.keys(this.state.products)
          .filter((id) => this.state.products[id] > 0)
          .map((id) => ({ id, qty: this.state.products[id], product: productMap.get(String(id)) }));

        if (this.summaryPreview) {
          this.summaryPreview.innerHTML = entries
            .map(({ product }) => (product && product.image
              ? `<img src="${product.image}" alt="" width="32" height="32" loading="lazy">`
              : ''))
            .join('');
        }

        if (this.summaryList) {
          const removeLabel = this.dataset.i18nRemove || 'Remove';
          this.summaryList.innerHTML = entries
            .map(({ id, qty, product }) => {
              const title = product ? this.escapeHtml(product.title) : '';
              return `<li class="byo-summary-list-item">
                <span class="byo-summary-list-title">${title}</span>
                <span class="byo-summary-list-qty">×${qty}</span>
                <button
                  type="button"
                  class="byo-summary-remove"
                  data-byo-summary-remove
                  data-product-id="${id}"
                  aria-label="${this.escapeHtml(removeLabel)} ${title}"
                >×</button>
              </li>`;
            })
            .join('');
        }

        if (this.ctaPrice) this.ctaPrice.textContent = this.state.selectedPrice || '';
        if (this.ctaButton) this.ctaButton.disabled = !this.validate();
      }

      toggleSummary() {
        const expanded = !!this.summaryToggle && this.summaryToggle.getAttribute('aria-expanded') === 'true';
        this.setSummaryExpanded(!expanded);
      }

      setSummaryExpanded(expanded) {
        if (this.summaryToggle) this.summaryToggle.setAttribute('aria-expanded', String(expanded));
        if (this.summaryPanel) this.summaryPanel.hidden = !expanded;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      hideError() {
        this.showError('');
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value == null ? '' : String(value);
        return div.innerHTML;
      }

      addToCart() {
        if (!this.validate() || this.isAdding) return;

        this.isAdding = true;
        this.hideError();
        if (this.ctaButton) this.ctaButton.disabled = true;
        if (this.ctaLabel) this.ctaLabel.textContent = this.dataset.i18nAdding || this.ctaLabel.textContent;

        const properties = {
          _bundle_type: 'BYOB',
          'Pack Size': String(this.state.packSize),
        };
        const productMap = new Map(this.data.products.map((product) => [String(product.id), product]));
        Object.keys(this.state.products).forEach((id) => {
          const qty = this.state.products[id];
          if (qty <= 0) return;
          const product = productMap.get(String(id));
          const title = product ? product.title : id;
          properties[`Flavour: ${title}`] = `×${qty}`;
        });

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const config = fetchConfig('javascript');
        const payload = {
          id: this.state.selectedVariantId,
          quantity: 1,
          properties,
        };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(payload);

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error('byo-box: add to cart failed', response);
              this.showError(response.description || this.dataset.i18nError);
              return;
            }

            if (this.cart) {
              if (response.sections && response.sections[this.cart.getSectionsToRender()[0].id]) {
                this.cart.renderContents(response);
                // CartDrawer.renderContents() only clears "is-empty" from the inner
                // .drawer-inner node, not the <cart-drawer> root - callers are expected
                // to do that themselves (see product-form.js). Without this, the CSS
                // rules scoped to "cart-drawer.is-empty"/"cart-drawer:not(.is-empty)"
                // keep the freshly injected item HTML hidden until a full page reload.
                if (this.cart.classList.contains('is-empty')) {
                  this.cart.classList.remove('is-empty');
                }
              } else {
                console.error('byo-box: cart add succeeded but no section HTML was returned', response);
                this.showError(this.dataset.i18nError);
              }
            }
            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'byo-box' });
            }
            this.selectPack(this.data.packs[0] && this.data.packs[0].id);
          })
          .catch((error) => {
            console.error('byo-box: add to cart request failed', error);
            this.showError(this.dataset.i18nError);
          })
          .finally(() => {
            this.isAdding = false;
            if (this.ctaLabel) this.ctaLabel.textContent = this.dataset.i18nAddToCart || this.ctaLabel.textContent;
            this.updateSummary();
          });
      }
    }
  );
}
